/**
 * USER-ROW TWINS (PR-F0, 2026-09-21 — Miles: "an already generated message
 * disappearing").
 *
 * ONE QUESTION, TWO ROWS. A person's own question sits in their tree under the
 * client's uuid id and carries `sharedMessageId` — the id the SAME question has in
 * the shared store, `prest0n_task_<taskId>__user`, minted before the bubble exists.
 * The store's copy of that question (the TWIN) can reach the owner tree as a row of
 * its own: the group listener adopts every echo while the local list is still empty
 * (a thread switch, a sign-in), and the merges that later meet the client copy key
 * on `id` and `taskAnchorId` only. From then on every hydrate rebuilds the pair —
 * both `seq 0` at the turn's instant — and a live snapshot draws the twin UNDER the
 * answer (an equal-instant foreign row was inserted after everything at that
 * instant). Read live on 2026-09-21 in the owner's own thread: nine pairs since
 * 09-17, the newest drawn under its answer after a re-login, so the answer read as
 * gone. Nothing was lost; the answer sat one row up.
 *
 * THE RULE IS THE ID LINK AND NOTHING ELSE. A user row whose `sharedMessageId`
 * names another row's id in the same list, and a server-shaped `…__user` row whose
 * id is some client row's `sharedMessageId`, are one question. Equal text is not a
 * link; an equal instant is not a link — two different messages can share both. The
 * CLIENT copy survives: it carries the bubble's html, the turn's `orderTs` / `seq`,
 * the reply ref and the attachment set; the twin is dropped, and its position goes
 * with it (the survivors keep their order — the sort that made it is untouched).
 * Assistant rows are never touched. A list with nothing to collapse comes back as
 * the SAME reference — the identity exit every caller depends on.
 *
 * Firebase-free on purpose: it runs inside the reducer-boundary hydrate path and the
 * repositories alike, and the chat-isolation battery must be able to load it without
 * `firebase/auth` touching global fetch at import time.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';

/** The shared store's id shape for a person's turn — the client twin of the server's `userTurnAnchorId`. */
const SERVER_USER_TWIN_ID_RE = /^prest0n_task_.+__user$/;

export function isServerUserTwinId(id: unknown): boolean {
  return typeof id === 'string' && SERVER_USER_TWIN_ID_RE.test(id.trim());
}

function idOf(m: ChatMessage | null | undefined): string {
  return m && typeof m.id === 'string' ? m.id.trim() : '';
}

/** A user row's link to the shared store: its `sharedMessageId`, trimmed, or ''. */
export function sharedIdOf(m: ChatMessage | null | undefined): string {
  return m && typeof m.sharedMessageId === 'string' ? m.sharedMessageId.trim() : '';
}

/**
 * The twin ids that a client copy in `list` claims: `sharedMessageId` values of user
 * rows that (a) name a DIFFERENT user row present in the list and (b) have the
 * server twin's shape. Exported so the merge seams share one verdict.
 */
export function claimedUserTwinIds(list: ReadonlyArray<ChatMessage>): Set<string> {
  // Only a USER row can be a twin: a link that names an assistant row (or nothing
  // in the list) is dangling, and a dangling link claims nothing.
  const ids = new Set<string>();
  for (const m of list) {
    const id = m && m.isUser === true ? idOf(m) : '';
    if (id) ids.add(id);
  }
  const claimed = new Set<string>();
  for (const m of list) {
    if (!m || m.isUser !== true) continue;
    const shared = sharedIdOf(m);
    if (!shared || shared === idOf(m)) continue;
    if (!isServerUserTwinId(shared)) continue;
    if (ids.has(shared)) claimed.add(shared);
  }
  return claimed;
}

/**
 * Drop every server twin that a client copy in the list claims. Pure; the same
 * reference when nothing collapses; survivors keep their relative order.
 */
export function collapseUserRowTwins<T extends ChatMessage>(list: ReadonlyArray<T>): T[] {
  const arr = Array.isArray(list) ? list : [];
  if (arr.length < 2) return arr as T[];
  const claimed = claimedUserTwinIds(arr);
  if (claimed.size === 0) return arr as T[];
  const out: T[] = [];
  for (const m of arr) {
    if (m && m.isUser === true && claimed.has(idOf(m))) continue;
    out.push(m);
  }
  return out;
}
